import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import ListingEditModal from "../components/ListingEditModal";
import ListingFilterPanel from "../components/filters/ListingFilterPanel";
import useListingFilters from "../components/filters/useListingFilters";
import { inputStyle, labelStyle } from "../components/filters/styles";
import { fromStored } from "../components/filters/filterSpec";
import OwnershipSelect from "../components/OwnershipSelect";

const PAGE_SIZE = 25;

// Opening a listing's Details page unmounts this one, so what the user searched has to live
// somewhere outside the component or "‹ Back to listings" drops them on a default board.
// Session storage (per tab) carries it across that round trip without pushing 24 filters
// through the URL. Only the APPLIED set is stored, so the controls and the rows on screen
// always agree after a restore — a half-typed filter never comes back as if it had run.
const STATE_KEY = "listings.search.state";
const readSavedState = () => {
  try { return JSON.parse(sessionStorage.getItem(STATE_KEY)) || null; } catch { return null; }
};
const EMPTY_LOCAL = { search: "", saleOrRental: "", ownershipType: [], statusFilter: "active", neighborhoodIds: [] };

export default function ListingSearchPage() {
  const navigate = useNavigate();
  // Read once, on mount: every piece of search state below starts from it, so the first
  // fetch already runs with the restored filters instead of the defaults.
  const [restored] = useState(readSavedState);
  const savedLocal = restored?.local || null;

  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(restored?.page || 1);
  const [loading, setLoading] = useState(true);

  // The page's own controls (address, type, ownership, status) stay local state; everything in
  // the shared spec lives in the filter hook. Both are staged and submitted together.
  const [searchInput, setSearchInput] = useState(savedLocal?.search || "");
  const [saleOrRental, setSaleOrRental] = useState(savedLocal?.saleOrRental || "");
  // Multi-select. Restored state may predate that, so a stored string is read as one choice.
  const [ownershipType, setOwnershipType] = useState(() => {
    const saved = savedLocal?.ownershipType;
    if (Array.isArray(saved)) return saved;
    return saved ? [saved] : [];
  });
  const [statusFilter, setStatusFilter] = useState(savedLocal?.statusFilter || "active");
  const [sortBy, setSortBy] = useState(restored?.sortBy || "created_date");
  const [sortDir, setSortDir] = useState(restored?.sortDir || "desc");
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState(savedLocal?.neighborhoodIds || []);
  const filters = useListingFilters(fromStored(restored?.filters || {}));

  // What the rows on screen were actually fetched with. Paging and re-sorting reuse it, so a
  // half-typed filter the user never submitted can't leak into page 2.
  const [appliedLocal, setAppliedLocal] = useState(savedLocal || EMPTY_LOCAL);

  const [editModalListing, setEditModalListing] = useState(null);

  useEffect(() => {
    authFetch("/sale-reviews/neighborhoods")
      .then((data) => setNeighborhoods(data.neighborhoods || []))
      .catch(() => {});
  }, []);

  // Fetch on page/sort changes and on an explicit Search (which bumps `submitted`).
  const [submitted, setSubmitted] = useState(0);
  useEffect(() => {
    loadListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortBy, sortDir, submitted]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const local = appliedLocal;
      // Snapshot exactly what this fetch runs with, so coming back reproduces these rows.
      try {
        sessionStorage.setItem(STATE_KEY, JSON.stringify({
          local, filters: filters.toStored(filters.applied), page, sortBy, sortDir,
        }));
      } catch { /* private mode / quota: the board still works, the state just won't survive */ }
      const params = new URLSearchParams({
        page, limit: PAGE_SIZE, status_filter: local.statusFilter,
        sort_by: sortBy, sort_dir: sortDir,
      });
      if (local.search) params.append("search", local.search);
      if (local.saleOrRental) params.append("sale_or_rental", local.saleOrRental);
      const ownership = Array.isArray(local.ownershipType)
        ? local.ownershipType
        : (local.ownershipType ? [local.ownershipType] : []);
      if (ownership.length > 0) params.append("ownership_type", ownership.join(","));
      if (local.neighborhoodIds.length > 0) params.append("neighborhood_ids", local.neighborhoodIds.join(","));
      filters.toParams(filters.applied, params);
      const data = await authFetch(`/listings/search?${params}`);
      setListings(data.listings || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("Error searching listings:", e);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    filters.apply();
    setAppliedLocal({
      search: searchInput.trim(), saleOrRental, ownershipType, statusFilter,
      neighborhoodIds: selectedNeighborhoods,
    });
    setPage(1);
    setSubmitted((n) => n + 1);
  };

  const resetFilters = () => {
    setSearchInput(""); setSaleOrRental(""); setOwnershipType([]);
    setStatusFilter("active"); setSortBy("created_date"); setSortDir("desc");
    setSelectedNeighborhoods([]);
    filters.load({});   // clears the controls AND the applied snapshot
    setAppliedLocal(EMPTY_LOCAL);
    setPage(1);
    setSubmitted((n) => n + 1);
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortDir("desc"); }
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const th = (field, label, sortable = true) => (
    <th
      onClick={sortable ? () => toggleSort(field) : undefined}
      style={{ textAlign: "left", padding: "8px 10px", fontSize: "11px", fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase", whiteSpace: "nowrap", cursor: sortable ? "pointer" : "default", userSelect: "none" }}
    >
      {label}{sortable && sortBy === field ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
    </th>
  );
  const td = { padding: "8px 10px", fontSize: "13px", color: "#333", borderBottom: "1px solid #eee", verticalAlign: "top" };
  const actionBtn = (bg) => ({ padding: "3px 9px", fontSize: "11px", backgroundColor: bg, color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" });

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />
      <div style={{ padding: "20px", maxWidth: "1700px", margin: "0 auto" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 700, margin: "0 0 16px 0", color: "#222" }}>Listings</h1>

        {/* Filters — the shared panel (components/filters/); `leading` carries the four
            controls that are this page's own rather than part of the shared spec. */}
        <ListingFilterPanel
          scope="listings"
          values={filters.values}
          set={filters.set}
          onSearch={applySearch}
          onReset={resetFilters}
          loading={loading}
          leading={
            <>
              <NeighborhoodSelect
                neighborhoods={neighborhoods}
                selected={selectedNeighborhoods}
                onChange={setSelectedNeighborhoods}
                placeholder="All Neighborhoods"
                gridColumn="span 2"
              />
              <div>
                <label style={labelStyle}>Address / Listing ID</label>
                <input type="text" placeholder="Search…" value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Type</label>
                <select value={saleOrRental} onChange={(e) => setSaleOrRental(e.target.value)}
                  style={{ ...inputStyle, backgroundColor: "#fff" }}>
                  <option value="">Sale & Rental</option>
                  <option value="S">Sale</option>
                  <option value="R">Rental</option>
                </select>
              </div>
              <OwnershipSelect selected={ownershipType} onChange={setOwnershipType} />
              <div>
                <label style={labelStyle}>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ ...inputStyle, backgroundColor: "#fff" }}>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                  <option value="all">All</option>
                </select>
              </div>
            </>
          }
        />

        {/* Count + pagination */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <div style={{ fontSize: "13px", color: "#666", fontWeight: 500 }}>
            {total > 0 ? `${((page - 1) * PAGE_SIZE + 1).toLocaleString()}–${Math.min(total, page * PAGE_SIZE).toLocaleString()} of ${total.toLocaleString()}` : "No results"}
          </div>
          <Pager page={page} totalPages={totalPages} loading={loading} setPage={setPage} />
        </div>

        {/* Results */}
        <div style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {th("address", "Address")}
                <th style={{ ...td, fontSize: 11, fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase" }}>Type</th>
                {th("neighborhood", "Neighborhood")}
                {th("bedrooms", "Bd/Ba/Sqft")}
                {th("list_price", "Price")}
                {th("predicted_rent", "Pred. Rent")}
                {th("created_date", "Listed")}
                <th style={{ ...td, fontSize: 11, fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase" }}>Status</th>
                <th style={{ ...td, fontSize: 11, fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ ...td, textAlign: "center", padding: 30, color: "#999" }}>Loading…</td></tr>
              ) : listings.length === 0 ? (
                <tr><td colSpan={9} style={{ ...td, textAlign: "center", padding: 30, color: "#999" }}>No listings match these filters.</td></tr>
              ) : (
                listings.map((l) => (
                  <tr key={l.id}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                    <td style={{ ...td, maxWidth: 340 }}>
                      <div style={{ fontWeight: 600, color: "#222" }}>
                        {l.address}{l.unit_number ? ` #${l.unit_number}` : ""}
                        {l.verified && (
                          <span title="Data verified" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#2e7d32", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap" }}>✓ Verified</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "#888" }}>{l.sale_or_rental === "S" ? "Sale" : "Rental"} · #{l.id}</div>
                    </td>
                    <td style={td}>{l.condo_coop}</td>
                    <td style={td}>{l.neighborhood || "—"}</td>
                    <td style={td}>
                      {(l.bedrooms ?? "?")}bd · {(l.bathrooms ?? "?")}ba
                      <div style={{ fontSize: 12, color: "#777" }}>{l.sqft ? l.sqft.toLocaleString() + " sqft" : "—"}</div>
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{l.list_price ? `$${l.list_price.toLocaleString()}` : "—"}</td>
                    <td style={td}>
                      {l.predicted_rent ? <><span style={{ fontWeight: 600, color: "#1976d2" }}>${l.predicted_rent.toLocaleString()}</span>{l.cap_rate != null && <div style={{ fontSize: 11, color: "#888" }}>cap {l.cap_rate}%</div>}</> : <span style={{ color: "#bbb" }}>—</span>}
                    </td>
                    <td style={td}>{l.created_date ? new Date(l.created_date).toLocaleDateString() : "—"}</td>
                    <td style={{ ...td, fontSize: 12 }}>{l.listing_status}</td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button style={actionBtn("#1976d2")} onClick={() => navigate(`/listing/${l.id}`)}>Details</button>
                        <button style={actionBtn("#455a64")} onClick={() => setEditModalListing({ id: l.id })}>Edit</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
          <Pager page={page} totalPages={totalPages} loading={loading} setPage={setPage} />
        </div>
      </div>

      {editModalListing && (
        <ListingEditModal
          listing={editModalListing}
          onClose={() => setEditModalListing(null)}
          onSave={() => { setEditModalListing(null); loadListings(); }}
          onVerify={() => { setEditModalListing(null); loadListings(); }}
        />
      )}
    </div>
  );
}

function Pager({ page, totalPages, loading, setPage }) {
  const btn = (disabled) => ({ padding: "5px 12px", fontSize: "13px", border: "1px solid #ccc", borderRadius: "4px", background: disabled ? "#f5f5f5" : "#fff", color: disabled ? "#aaa" : "#333", cursor: disabled ? "not-allowed" : "pointer" });
  const atStart = page <= 1, atEnd = page >= totalPages;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <button style={btn(atStart || loading)} disabled={atStart || loading} onClick={() => setPage(page - 1)}>‹ Prev</button>
      <span style={{ fontSize: "13px", color: "#666" }}>Page {page} of {totalPages}</span>
      <button style={btn(atEnd || loading)} disabled={atEnd || loading} onClick={() => setPage(page + 1)}>Next ›</button>
    </div>
  );
}
